import { useCallback, useMemo, useState } from 'react';
import { useProjectIceFacade, useStartupPageFacade } from '@ideoz/core';

/**
 * Logic for the Work Scopes module: wraps the shared startup facade (scope
 * list, filters, create/edit dialog), the project's ICE scores, and owns the
 * delete-confirmation and ICE-details dialog state.
 */
export function useWorkScopes(params: {
  startupId: string;
  navigateToScope: (scopeId: string) => void;
}) {
  const facade = useStartupPageFacade({
    navigateToScope: params.navigateToScope,
    startupId: params.startupId,
  });

  // Reading scores never computes one, so this is safe on every load.
  const ice = useProjectIceFacade({ projectId: params.startupId });

  const [scopeToDelete, setScopeToDelete] = useState<{ id: string; title: string } | null>(null);
  const [iceScopeId, setIceScopeId] = useState<string | null>(null);

  const requestDeleteScope = useCallback(
    (id: string) => {
      const scope = facade.filteredScopes.find((s) => s.id === id);
      if (scope) setScopeToDelete({ id: scope.id, title: scope.title });
    },
    [facade.filteredScopes]
  );

  const cancelDeleteScope = useCallback(() => setScopeToDelete(null), []);

  const confirmDeleteScope = useCallback(async () => {
    if (!scopeToDelete) return;
    await facade.deleteScope(scopeToDelete.id);
    setScopeToDelete(null);
  }, [scopeToDelete, facade]);

  /** Scope rows carry the ICE composite the ranking reports for them. */
  const scopesWithIce = useMemo(
    () =>
      facade.filteredScopes.map((scope) => ({
        ...scope,
        iceScore: ice.scoresByScopeId[scope.id]?.ice ?? null,
      })),
    [facade.filteredScopes, ice.scoresByScopeId]
  );

  /**
   * Open the details popup. The card is fetched on open rather than for every
   * row up front — the list only needs the composite, which the ranking
   * already carries in one call.
   */
  const openIceDetails = useCallback(
    (scopeId: string) => {
      setIceScopeId(scopeId);
      void ice.loadCard(scopeId);
    },
    [ice]
  );

  const closeIceDetails = useCallback(() => setIceScopeId(null), []);

  const iceDialog = useMemo(() => {
    if (!iceScopeId) {
      return { scopeId: null, scopeTitle: undefined, data: null, isLoading: false, isScoring: false, error: null };
    }
    return {
      scopeId: iceScopeId,
      scopeTitle: facade.filteredScopes.find((s) => s.id === iceScopeId)?.title,
      data: ice.cardsByScopeId[iceScopeId] ?? null,
      isLoading: ice.loadingCardScopeIds.includes(iceScopeId),
      isScoring: ice.scoringScopeIds.includes(iceScopeId),
      error: ice.errorsByScopeId[iceScopeId] ?? null,
    };
  }, [
    iceScopeId,
    facade.filteredScopes,
    ice.cardsByScopeId,
    ice.loadingCardScopeIds,
    ice.scoringScopeIds,
    ice.errorsByScopeId,
  ]);

  return {
    facade,
    ice,
    scopesWithIce,
    iceDialog,
    openIceDetails,
    closeIceDetails,
    scopeToDelete,
    requestDeleteScope,
    cancelDeleteScope,
    confirmDeleteScope,
  };
}
